'use client';

import { Modal, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgAdd, SvgLink } from '@common/icon';
import { cn, isDev } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { UnauthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import {
  BREAKPOINTS,
  ENV,
  ROUTES,
  WELLO_IMAGE_PREFIX_URL,
} from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useClipboard } from '@/hooks/useClipboard';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

const INVITE_OG_IMAGE_URL = `${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/invite-kakao-web-og.png`;

const INVITE_SHARE_CONFIG = {
  thumbnailImageUrl: `${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/invite-kakao-app-og.png`,
  buttonText: '지금 바로 선물 받기',
};

interface FriendInviteShareDrawerProps {
  opened: boolean;
  onClose?: () => void;
}

const generateInviteShareUrl = (memberCode?: string) => {
  let baseDomain = ENV.NEXT_PUBLIC_DOMAIN;

  if (!isDev) {
    baseDomain = baseDomain.replace('https://', 'https://www.');
  }

  const fullShareUrl = new URL('/donation/event/market', baseDomain);

  if (memberCode) {
    fullShareUrl.searchParams.set('utm_source', 'wello');
    fullShareUrl.searchParams.set('utm_medium', 'eventButton');
    fullShareUrl.searchParams.set('utm_campaign', 'kakaoshare1129');
    fullShareUrl.searchParams.set('utm_content', 'event251129');
    fullShareUrl.searchParams.set('inviterCode', memberCode);
  }

  return fullShareUrl.href;
};

export const FriendInviteShareDrawer = ({
  opened,
  onClose,
}: FriendInviteShareDrawerProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const [webviewType, osType] = useDeviceInfoStore(
    useShallow((state) => [state.webviewType, state.osType]),
  );

  const { isWelloApp } = useCheckWelloApp();
  const { isInitialized } = useKakaoShare();

  const [isLogin, memberCode] = useAuthStore(
    useShallow((state) => [state.isLogin, state.myInfo?.memberCode]),
  );

  const { data: userName } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home-donation-event-history/member-name'
    ]({
      query: {
        memberCode: memberCode as string,
      },
    }),
    enabled: !!memberCode,
    select: ({ context }) => context?.name,
  });

  const inviteShareUrl = generateInviteShareUrl(memberCode);

  const copy = useClipboard();
  const router = useCustomRouter();
  const currentPathname = usePathname();
  const { toast } = useToast();

  const {
    request: checkKakaoInstallRequest,
    response: checkKakaoInstallResponse,
  } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED,
  });

  const { request: requestShare, isPending } = useHandleApp({
    type: MESSAGE_KEYWORD.SHARE_CONTENT,
  });

  const isMobileWeb =
    !webviewType && osType && ['ios', 'android'].includes(osType);

  useEffect(() => {
    if (opened) {
      checkKakaoInstallRequest();
    }
  }, [checkKakaoInstallRequest, opened]);

  const handleKakaoClickInApp = async () => {
    if (isPending) return;

    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: currentPathname || '' },
        }),
      );
      onClose?.();

      return;
    }

    if (checkKakaoInstallResponse?.isSuccess) {
      const title = userName
        ? `${userName}님이 최대 135만원의 12월 혜택을 선물했어요!`
        : '최대 135만원의 12월 혜택을 선물했어요!';
      const description =
        '친구 초대장으로 가입하면\n가입 즉시 1,000원을 받을 수 있어요.';

      requestShare({
        shareType: 'kakao',
        url: inviteShareUrl,
        thumbnail: INVITE_SHARE_CONFIG.thumbnailImageUrl,
        subject: title,
        description,
      });
      onClose?.();
    } else {
      toast({
        message: '카카오톡이 설치되어 있지 않아요',
        type: 'fail',
      });
    }
  };

  const handleKakaoClickInWeb = async () => {
    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: currentPathname || '' },
        }),
      );
      onClose?.();

      return;
    }

    const title = userName
      ? `${userName}님이 최대 135만원의 12월 혜택을 선물했어요!`
      : '최대 135만원의 12월 혜택을 선물했어요!';

    window.Kakao?.Share?.sendDefault?.({
      objectType: 'feed',
      content: {
        title,
        description:
          '친구 초대장으로 가입하면\n가입 즉시 1,000원을 받을 수 있어요.',
        imageUrl: INVITE_OG_IMAGE_URL,
        imageWidth: 1600,
        imageHeight: 800,
        link: {
          mobileWebUrl: inviteShareUrl,
          webUrl: inviteShareUrl,
        },
      },
      buttons: [
        {
          title: INVITE_SHARE_CONFIG.buttonText,
          link: {
            mobileWebUrl: inviteShareUrl,
            webUrl: inviteShareUrl,
          },
        },
      ],
    });
    onClose?.();
  };

  const handleCopyClick = () => {
    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: currentPathname || '' },
        }),
      );
      onClose?.();

      return;
    }

    try {
      copy({ value: inviteShareUrl });
    } finally {
      onClose?.();
    }
  };

  const handleMoreClick = () => {
    if (!isLogin) {
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: currentPathname || '' },
        }),
      );
      onClose?.();

      return;
    }

    try {
      if (isWelloApp) {
        requestShare({
          shareType: 'uni',
          url: inviteShareUrl,
        });

        return;
      }

      if (isMobileWeb) {
        navigator.share({
          url: inviteShareUrl,
        });

        return;
      }
    } finally {
      onClose?.();
    }
  };

  const content = (
    <div
      className={cn('mx-42 flex justify-around gap-6', {
        'justify-between': isWelloApp,
      })}
    >
      {webviewType !== WEBVIEW_TYPE.KB_PAY ? (
        <KakaoButton
          isLoading={!isInitialized}
          onClick={isWelloApp ? handleKakaoClickInApp : handleKakaoClickInWeb}
        />
      ) : null}
      <button
        type="button"
        className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
        onClick={handleCopyClick}
      >
        <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-500 text-Gray-white">
          <SvgLink />
        </div>
        <h4 className="font-400 text-Gray-900 typo-body4">URL 복사</h4>
      </button>
      {isWelloApp || isMobileWeb ? (
        <button
          type="button"
          className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
          onClick={handleMoreClick}
        >
          <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-200 text-Gray-black">
            <SvgAdd />
          </div>
          <h4 className="font-400 text-Gray-900 typo-body4">더 보기</h4>
        </button>
      ) : null}
    </div>
  );

  if (isDesktopViewport) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        asChild
        className="w-full max-w-[480px]"
        closeButton
      >
        <div>{content}</div>
      </Modal>
    );
  }

  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      drawerType="bottom"
      drawerNoPadding
    >
      <div className="pb-16">{content}</div>
    </ResponsiveDialog>
  );
};

interface KakaoButtonProps {
  isLoading: boolean;
  onClick: () => void;
}

const KakaoButton = ({ isLoading, onClick }: KakaoButtonProps) => {
  return (
    <button
      type="button"
      disabled={isLoading}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-6 border-none',
        { 'cursor-not-allowed opacity-50': isLoading },
      )}
      onClick={onClick}
    >
      <div className="flex size-48 items-center justify-center rounded-pill bg-[#fbdf03]">
        {isLoading ? (
          <Spinner size={24} />
        ) : (
          <Image
            alt="kakao"
            height={24}
            src="/assets/pngs/login/kakao.png"
            width={24}
          />
        )}
      </div>
      <span className="font-400 text-Gray-900 typo-body4">카카오톡</span>
    </button>
  );
};
